import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { Page } from 'styles';
import { BackLink } from 'components/back-link';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { Spotlight } from './spotlight';
import { Stats } from './stats';
import { WIND_PROJECT_QUERY } from './wind-details.api';

const getCopy = copyFor('usage.windDetails');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

export const WindDetails = () => {
  const { planId, projectId } =
    useParams<{ planId?: string; projectId: string }>();
  const { usage } = routes({ planId });
  const {
    data: impactStats,
    error: impactError,
    loading: impactLoading,
  } = useImpactStats({ planId: Number(planId) });
  const { data, error, loading } = useQuery(WIND_PROJECT_QUERY, {
    variables: { id: Number(projectId) },
  });

  if (loading || impactLoading) return <LoadingPage />;
  if (error || impactError || !impactStats) {
    return <ErrorPage title={getCopy('title')} to={usage} />;
  }

  const project = data.featuredWindProject;
  const { carbonEquivalency, totalYearlyPowerProduced, unitsPowered } = project;
  const aggregateKwh = totalYearlyPowerProduced * 1000; // multiply by 1000 to convert from mWh to kWh

  return (
    <>
      <BackLink to={usage} location={getBackLinkCopy('usage')} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        <Text opacity="medium" margin={{ bottom: '24px' }}>
          {getCopy('subtitle', {
            kwh: Math.round(impactStats.carbonEquivalency.kwh),
          })}
        </Text>
        <Spotlight project={project} />
        <Stats
          CO2={Math.round(carbonEquivalency.totalCarbon)}
          kWh={aggregateKwh}
          homes={unitsPowered}
        />
      </Page>
    </>
  );
};
